import { useCallback, useEffect, useRef, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Upload, Trash2, PhoneCall, CheckCircle2 } from "lucide-react";
import { TcpaDialog } from "@/components/PmAutopilot";

const Agents = ({ rows, act }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-xs" data-testid="rp-agents-table">
      <thead><tr className="text-left uppercase text-slate-500"><th className="py-2 pr-3">Buyer's agent</th><th className="pr-3">Phone</th><th className="pr-3">Email</th><th className="pr-3">Days</th><th className="pr-3">Calls / texts / emails</th><th className="pr-3">Status</th><th /></tr></thead>
      <tbody className="divide-y divide-slate-100">
        {rows.map((a) => (
          <tr key={a.id} data-testid={`rp-agent-${a.id}`}>
            <td className="py-2 pr-3 font-semibold text-slate-800">{a.name}<div className="font-normal text-slate-500">{a.business}</div></td>
            <td className="pr-3">{a.phone}</td><td className="break-all pr-3">{a.email}</td><td className="pr-3">{a.days} / 7</td>
            <td className="pr-3">{a.calls} / {a.texts} / {a.emails}</td><td className="pr-3">{a.status}</td>
            <td><Button size="icon" variant="ghost" onClick={() => act(() => api.delete(`/rp/agents/${a.id}`), "Removed")} data-testid={`rp-agent-delete-${a.id}`}><Trash2 className="h-4 w-4 text-red-600" /></Button></td>
          </tr>))}
      </tbody>
    </table>
  </div>
);

const Listing = ({ x, act }) => {
  const file = useRef(null);
  const upload = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    await act(() => api.post(`/rp/listings/${x.id}/import`, fd), null, (d) => `Added ${d.added} buyer's agents${d.duplicates ? `, ${d.duplicates} already on the list` : ""}${d.skipped.length ? `, ${d.skipped.length} skipped (no name, phone or email)` : ""}`);
  };
  return (
    <Card className="mb-4 p-5" data-testid={`rp-listing-${x.id}`}>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div><div className="font-display text-lg font-bold text-slate-900">{x.address}</div>
          <Badge className={`border-0 ${x.status === "sold" ? "bg-slate-200 text-slate-700" : "bg-emerald-50 text-emerald-800"}`}>{x.status === "sold" ? "Sold - contacting stopped" : `${x.agents.length} buyer's agents`}</Badge></div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => file.current?.click()} data-testid={`rp-upload-${x.id}`}><Upload className="mr-1 h-4 w-4" />Upload buyer's agents</Button>
          <input ref={file} type="file" accept=".csv,.xlsx" className="hidden" onChange={upload} data-testid={`rp-file-${x.id}`} />
          {x.status !== "sold" && <Button variant="outline" size="sm" onClick={() => window.confirm("Mark this listing sold? Ava stops contacting its buyer's agents.") && act(() => api.put(`/rp/listings/${x.id}`, { status: "sold" }), "Marked sold")} data-testid={`rp-sold-${x.id}`}><CheckCircle2 className="mr-1 h-4 w-4" />Mark sold</Button>}
          {x.status === "sold" && <Button variant="outline" size="sm" onClick={() => act(() => api.put(`/rp/listings/${x.id}`, { status: "active" }), "Listing reopened")} data-testid={`rp-reopen-${x.id}`}>Reopen</Button>}
          <Button variant="ghost" size="icon" onClick={() => window.confirm("Delete this listing and its buyer's agents?") && act(() => api.delete(`/rp/listings/${x.id}`), "Deleted")} data-testid={`rp-delete-${x.id}`}><Trash2 className="h-4 w-4 text-red-600" /></Button>
        </div>
      </div>
      {x.agents.length ? <Agents rows={x.agents} act={act} /> : <p className="text-xs text-slate-500">Upload a CSV or Excel file with the buyer's agents' name, phone, email (and brokerage).</p>}
    </Card>
  );
};

export default function ReverseProspecting() {
  const [d, setD] = useState(null);
  const [addr, setAddr] = useState("");
  const [tcpa, setTcpa] = useState(null);
  const load = useCallback(() => api.get("/rp").then((r) => setD(r.data)).catch((e) => toast.error(apiError(e))), []);
  useEffect(() => { load(); }, [load]);
  const act = async (fn, msg, fmt) => {
    try { const r = await fn(); if (r.data?.listings) setD(r.data); else load(); const m = fmt ? fmt(r.data) : msg; if (m) toast.success(m); return true; }
    catch (e) { toast.error(apiError(e)); return false; }
  };
  const sign = async (name) => {
    const ok = tcpa === "auto" ? await act(() => api.put("/pm/autopilot", { kind: "rp", dial: true, email: true, tcpa_agree: true, tcpa_name: name }), "Autopilot on - Ava contacts them daily for 7 days")
      : await act(() => api.post("/rp/contact-now", { tcpa_agree: true, tcpa_name: name }), null, (r) => `Emailing ${r.emails} and calling/texting ${r.calls} buyer's agents now${r.calls_paused ? " (calls and texts wait for Mon-Sat 9 AM-6 PM ET)" : ""}`);
    if (ok) setTcpa(null);
  };
  if (!d) return null;
  return (
    <div className="mx-auto max-w-6xl" data-testid="rp-page">
      <h1 className="font-display text-3xl font-extrabold text-slate-900">Reverse Prospecting</h1>
      <p className="mb-5 text-sm text-slate-600">Add each listing and upload the buyer's agents who sent buyers for it. Ava emails, calls, leaves voicemails and texts them every day for 7 days, from a different number each day (local area codes first).</p>
      <Card className="mb-5 flex flex-wrap items-center justify-between gap-3 p-5" data-testid="rp-controls">
        <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">Autopilot
          <Switch checked={!!d.autopilot} disabled={d.dialing_disabled && !d.autopilot} onCheckedChange={(v) => (v ? setTcpa("auto") : act(() => api.put("/pm/autopilot", { kind: "rp", dial: false, email: false }), "Autopilot off"))} data-testid="rp-autopilot-switch" /></label>
        <Button className="gold-cta border-0 font-bold" onClick={() => setTcpa("now")} data-testid="rp-contact-now"><PhoneCall className="mr-2 h-4 w-4" />Contact all immediately</Button>
      </Card>
      <Card className="mb-5 flex flex-wrap gap-2 p-5">
        <Input className="min-w-0 flex-1" value={addr} onChange={(e) => setAddr(e.target.value)} placeholder="Listing address, e.g. 123 Main St, Orlando, FL 32801" data-testid="rp-add-address" />
        <Button onClick={async () => { if (await act(() => api.post("/rp/listings", { address: addr }), "Listing added")) setAddr(""); }} disabled={addr.trim().length < 8} data-testid="rp-add-listing"><Plus className="mr-1 h-4 w-4" />Add listing</Button>
      </Card>
      {d.listings.map((x) => <Listing key={x.id} x={x} act={act} />)}
      {!d.listings.length && <p className="text-center text-sm text-slate-500" data-testid="rp-empty">No listings yet.</p>}
      <TcpaDialog open={!!tcpa} onClose={() => setTcpa(null)} text={d.tcpa_text} onSign={sign} title="Permission to contact" />
    </div>
  );
}
